'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Store,
  MessageCircle,
  LayoutDashboard,
  Rss,
  Menu,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrentBusiness } from '@/lib/business-context';
import { useUnreadMessages } from '@/lib/use-unread-messages';
import { haptics } from '@/lib/native';
import { useFeatureFlag } from '@/lib/feature-flags';

interface BottomNavProps {
  onMoreClick: () => void;
}

const tabs = [
  { name: 'Feed', href: '/feed', icon: Rss, match: (p: string) => p === '/feed' },
  { name: 'Marketplace', short: 'Market', href: '/marketplace', icon: Store, match: (p: string) => p === '/marketplace' || p.startsWith('/buy') || p === '/products' || p.startsWith('/products/') || p.startsWith('/sell/catalog') || p.startsWith('/s/') },
  { name: 'Messages', href: '/messages', icon: MessageCircle, match: (p: string) => p === '/messages' || p.startsWith('/messages/') },
  // Sales is in More, including its /dashboard/sales alias.
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    match: (p: string) =>
      p === '/dashboard' || (p.startsWith('/dashboard/') && !p.startsWith('/dashboard/sales')),
  },
] as const;

export function BottomNav({ onMoreClick }: BottomNavProps) {
  const pathname = usePathname();
  const feedEnabled = useFeatureFlag('nav.feed');
  const visibleTabs = tabs.filter(tab => tab.href !== '/feed' || feedEnabled);
  const { currentBusinessId, businesses, loading: bizLoading } = useCurrentBusiness();
  const isLoggedIn = Boolean(currentBusinessId && (businesses.length > 0 || bizLoading));
  const { unreadCount: unreadMessageCount } = useUnreadMessages(isLoggedIn);

  // Check if "More" should be highlighted (any sidebar-only route is active)
  const isMoreActive = !visibleTabs.some((tab) => tab.match(pathname)) &&
    pathname !== '/' &&
    !pathname.startsWith('/marketplace') &&
    !pathname.startsWith('/login') &&
    !pathname.startsWith('/signup');

  const slotCount = isLoggedIn ? visibleTabs.length + 1 : visibleTabs.length;
  const activeIndex = (() => {
    const i = visibleTabs.findIndex((tab) => tab.match(pathname));
    if (i >= 0) return i;
    return isLoggedIn && isMoreActive ? visibleTabs.length : -1;
  })();

  return (
    <nav className="app-bottom-nav ios-bar app-island fixed z-40 md:hidden">
      {/* Floating island: sits above the home indicator with a guaranteed
          margin (see --app-island-bottom), content scrolls beneath the blur. */}
      <div className="relative flex h-[60px] items-stretch px-1">
        {/* Sliding active pill — one element that glides between tabs instead
            of a colour that just switches, the same cue iOS/Material tab bars use.
            It lives in a layer with the row's own padding so its 1/n width
            lines up exactly with the 1/n flex slots (a pill measured against
            the padding box sits a few px left of its tab). */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 px-1 py-1.5">
          <div className="relative h-full w-full">
            <div
              className="nav-pill"
              style={{
                width: `calc(100% / ${slotCount})`,
                transform: `translateX(${Math.max(0, activeIndex) * 100}%)`,
                opacity: activeIndex < 0 ? 0 : 1,
              }}
            >
              <div className="h-full w-full rounded-2xl bg-primary/20" />
            </div>
          </div>
        </div>

        {visibleTabs.map((tab) => {
          const isActive = tab.match(pathname);
          const showBadge = tab.name === 'Messages' && unreadMessageCount > 0;

          return (
            <Link
              key={tab.name}
              href={tab.href}
              prefetch={tab.href === '/feed' ? false : undefined}
              onClick={() => { if (!isActive) void haptics.selection(); }}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative z-10 flex h-full flex-1 basis-0 min-w-0 flex-col items-center justify-center gap-1 text-[10px] font-medium leading-none transition-colors',
                isActive ? 'text-foreground' : 'text-muted-foreground active:text-foreground',
              )}
            >
              <span key={isActive ? 'on' : 'off'} className={cn('relative', isActive && 'nav-icon-pop')}>
                <tab.icon className="h-5 w-5" strokeWidth={isActive ? 2.4 : 2} />
                {showBadge && (
                  <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none text-destructive-foreground animate-in zoom-in-50 duration-200">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </span>
              <span className={cn('block w-full truncate px-0.5 text-center', isActive && 'font-semibold')}>
                {'short' in tab ? tab.short : tab.name}
              </span>
            </Link>
          );
        })}

        {/* More tab — signed-in only: the drawer it opens is the app nav
            (My Stores, Sales, Sign Out), and /marketplace and /checkout are
            public routes, so an anonymous visitor could open it. */}
        {isLoggedIn && (
        <button
          type="button"
          aria-current={isMoreActive ? 'page' : undefined}
          onClick={() => { void haptics.light(); onMoreClick(); }}
          className={cn(
            'relative z-10 flex h-full flex-1 basis-0 min-w-0 flex-col items-center justify-center gap-1 text-[10px] font-medium leading-none transition-colors',
            isMoreActive ? 'text-foreground' : 'text-muted-foreground active:text-foreground',
          )}
        >
          <Menu className="h-5 w-5" strokeWidth={isMoreActive ? 2.4 : 2} />
          <span className={cn('block w-full truncate px-0.5 text-center', isMoreActive && 'font-semibold')}>
            More
          </span>
        </button>
        )}
      </div>
    </nav>
  );
}
